import { useLayoutEffect, useRef, useState } from 'react'
import { downloadMailFile, saveMailAttachment, setMailSeen } from '../services/mail'
import type { MailFlagsResult, MailMessageDetail, LogicalMailAttachment, MailAttachment } from '../services/mail'

export type MailMessageActionState =
  | { status: 'loading' }
  | { status: 'error'; error: unknown }
  | { status: 'ready'; result: MailFlagsResult | { id: number; created: boolean } | undefined }

interface Permissions { read: boolean; sync: boolean; active: boolean }

export interface MailActionSource {
  id: string; mailbox: string; availability: MailMessageDetail['availability']; attachments: Array<MailAttachment | LogicalMailAttachment>
  source_sha256?: string; seen?: boolean | null
}
// One instance per card. scopeKey must include user/tenant/mailbox/thread/logical identity.
// Uses physical endpoints only; the future logical adapter must provide the current authorized source.
export function useMailMessageActions(scopeKey: string, source: MailActionSource | null, permissions: Permissions) {
  const identity = JSON.stringify([scopeKey, source?.id, source?.mailbox, source?.availability,
    source?.attachments.map(item => [item.id, item.availability, item.filename]), permissions])
  const generation = useRef(0)
  const active = useRef(false)
  const running = useRef(new Set<string>())
  const [state, setState] = useState<{ identity: string; actions: Record<string, MailMessageActionState> }>({ identity, actions: {} })

  useLayoutEffect(() => {
    active.current = true
    generation.current++
    running.current.clear()
    setState({ identity, actions: {} })
    return () => { active.current = false; generation.current++; running.current.clear() }
  }, [identity])

  const run = async (key: string, allowed: boolean, work: () => Promise<MailFlagsResult | { id: number; created: boolean } | void>) => {
    if (!active.current || !allowed || !source || !permissions.read || running.current.has(key)) return
    const current = generation.current
    const valid = () => active.current && generation.current === current
    const publish = (value: MailMessageActionState) => {
      if (valid()) setState(previous => ({ identity, actions: { ...previous.actions, [key]: value } }))
    }
    running.current.add(key)
    publish({ status: 'loading' })
    try {
      const result = await work()
      publish({ status: 'ready', result: result || undefined })
      return valid() ? result : undefined
    } catch (error) { publish({ status: 'error', error }) }
    finally { if (valid()) running.current.delete(key) }
  }

  const attachmentAvailable = (id: string) => !!source?.attachments.some(item => item.id === id && item.availability === 'complete')
    && source.availability !== 'expired'

  return {
    actions: state.identity === identity ? state.actions : {},
    downloadSource: () => {
      const id = source?.id || ''
      return run('source', !!source?.source_sha256 && source.availability !== 'expired', () => downloadMailFile('source', id, 'messaggio.eml'))
    },
    downloadAttachment: (attachmentId: string) => {
      const attachment = source?.attachments.find(item => item.id === attachmentId)
      return run(`download:${attachmentId}`, attachmentAvailable(attachmentId),
        () => downloadMailFile('attachment', attachmentId, attachment?.filename || 'allegato'))
    },
    setSeen: (seen: boolean) => {
      const id = source?.id || ''
      return run('seen', permissions.sync && permissions.active && typeof source?.seen === 'boolean',
        () => setMailSeen(id, seen))
    },
    exportAttachment: (attachmentId: string, destination: { spaceId: number; parentId: number | null; canCreate: boolean }) => {
      const { spaceId, parentId, canCreate } = destination
      return run(`export:${attachmentId}`, attachmentAvailable(attachmentId) && canCreate && Number.isSafeInteger(spaceId) && spaceId > 0
        && (parentId === null || (Number.isSafeInteger(parentId) && parentId > 0)),
      () => saveMailAttachment(attachmentId, spaceId, parentId))
    },
  }
}
